/**
 * Manage Policies Screen
 * Super Admin can create and view policies
 */

import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, TextInput, Alert, ActivityIndicator } from 'react-native';
import { Card } from '@app/design-system';
import { policyService, type Policy, type CreatePolicyRequest, useAuthStore, getConfig } from '@app/core';
import { useNavigate } from 'react-router-dom';

const POLICY_TYPES: Array<'TERMS' | 'PRIVACY' | 'GRIEVANCE'> = ['TERMS', 'PRIVACY', 'GRIEVANCE'];

const POLICY_LABELS = {
  TERMS: 'Terms & Conditions',
  PRIVACY: 'Privacy Policy',
  GRIEVANCE: 'Grievance Redressal'
};

export const ManagePolicies: React.FC = () => {
  const navigate = useNavigate();
  const { tokens } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [latestPolicies, setLatestPolicies] = useState<Record<string, Policy | null>>({});

  // Form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState<CreatePolicyRequest>({
    type: 'TERMS',
    version: '',
    content: ''
  });

  // File upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    loadLatestPolicies();
  }, []);

  const loadLatestPolicies = async () => {
    setLoading(true);
    try {
      const policies: Record<string, Policy | null> = {};

      for (const type of POLICY_TYPES) {
        const policy = await policyService.getLatestPolicy(type);
        policies[type] = policy;
      }

      setLatestPolicies(policies);
    } catch (error) {
      console.error('Failed to load policies:', error);
      Alert.alert('Error', 'Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePolicy = async () => {
    if (!selectedFile) {
      Alert.alert('Error', 'Please select a PDF file');
      return;
    }

    // Validate PDF file
    if (selectedFile.type !== 'application/pdf') {
      Alert.alert('Error', 'Only PDF files are allowed');
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (selectedFile.size > maxSize) {
      Alert.alert('Error', 'File size must be less than 10MB');
      return;
    }

    // Get userId from tokens
    const createdBy = tokens?.userId;
    if (!createdBy) {
      Alert.alert('Error', 'User not authenticated');
      return;
    }

    setLoading(true);
    try {
      // Create policy with PDF file (backend will auto-generate version)
      await policyService.createPolicy(formData.type, selectedFile, createdBy);

      Alert.alert('Success', 'Policy created successfully');
      setShowAddForm(false);
      setFormData({ type: 'TERMS', version: '', content: '' });
      setSelectedFile(null);
      setUploadProgress(0);
      loadLatestPolicies();
    } catch (error: any) {
      console.error('Failed to create policy:', error);
      Alert.alert('Error', error.response?.data?.message || 'Failed to create policy');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      console.log('📄 [ManagePolicy] File selected:', {
        name: file.name,
        type: file.type,
        size: `${(file.size / 1024 / 1024).toFixed(2)} MB`
      });
      setSelectedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setUploadProgress(0);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigate('/dashboard')} style={styles.backButton}>
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>
        <Text style={styles.title}>Manage Policies</Text>
      </View>

      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        {/* Add New Policy Section */}
        <Card style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Add New Policy</Text>
            <TouchableOpacity
              onPress={() => setShowAddForm(!showAddForm)}
              style={styles.toggleButton}
            >
              <Text style={styles.toggleButtonText}>
                {showAddForm ? '− Hide Form' : '+ Add Policy'}
              </Text>
            </TouchableOpacity>
          </View>

          {showAddForm && (
            <View style={styles.form}>
              {/* Policy Type */}
              <View style={styles.formGroup}>
                <Text style={styles.label}>Policy Type *</Text>
                <View style={styles.pickerContainer}>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                    style={styles.picker as any}
                  >
                    {POLICY_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {POLICY_LABELS[type]}
                      </option>
                    ))}
                  </select>
                </View>
              </View>

              {/* PDF File Upload */}
              <View style={styles.formGroup}>
                <Text style={styles.label}>Upload Policy PDF *</Text>
                <Text style={styles.helperText}>Maximum file size: 10MB</Text>

                {!selectedFile ? (
                  <TouchableOpacity
                    onPress={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'application/pdf';
                      input.onchange = handleFileSelect as any;
                      input.click();
                    }}
                    style={styles.uploadButton}
                  >
                    <Text style={styles.uploadButtonText}>📄 Choose PDF File</Text>
                  </TouchableOpacity>
                ) : (
                  <View style={styles.filePreview}>
                    <View style={styles.fileInfo}>
                      <Text style={styles.fileName}>📄 {selectedFile.name}</Text>
                      <Text style={styles.fileSize}>
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </Text>
                    </View>
                    <TouchableOpacity onPress={handleRemoveFile} style={styles.removeButton}>
                      <Text style={styles.removeButtonText}>✕ Remove</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>

              {/* Submit Button */}
              <TouchableOpacity
                onPress={handleCreatePolicy}
                style={[styles.submitButton, loading && styles.submitButtonDisabled]}
                disabled={loading}
              >
                {loading ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <Text style={styles.submitButtonText}>Create Policy</Text>
                )}
              </TouchableOpacity>
            </View>
          )}
        </Card>

        {/* Latest Policies Section */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Latest Policies (View Only)</Text>

          {loading && !showAddForm ? (
            <ActivityIndicator size="large" color="#007AFF" style={styles.loader} />
          ) : (
            <View style={styles.policiesGrid}>
              {POLICY_TYPES.map((type) => {
                const policy = latestPolicies[type];
                return (
                  <View key={type} style={styles.policyCard}>
                    <Text style={styles.policyType}>{POLICY_LABELS[type]}</Text>

                    {policy ? (
                      <>
                        <View style={styles.policyInfo}>
                          <Text style={styles.policyLabel}>Version:</Text>
                          <Text style={styles.policyValue}>{policy.version}</Text>
                        </View>

                        <View style={styles.policyInfo}>
                          <Text style={styles.policyLabel}>Created:</Text>
                          <Text style={styles.policyValue}>{formatDate(policy.createdAt)}</Text>
                        </View>

                        <View style={styles.policyInfo}>
                          <Text style={styles.policyLabel}>Created By:</Text>
                          <Text style={styles.policyValue}>{policy.createdBy}</Text>
                        </View>

                        <View style={styles.policyInfo}>
                          <Text style={styles.policyLabel}>Status:</Text>
                          <View style={[styles.statusBadge, policy.active && styles.statusBadgeActive]}>
                            <Text style={[styles.statusText, policy.active && styles.statusTextActive]}>
                              {policy.active ? 'Active' : 'Inactive'}
                            </Text>
                          </View>
                        </View>

                        <View style={styles.policyInfo}>
                          <Text style={styles.policyLabel}>Document:</Text>
                          <Text style={styles.policyValue}>
                            {policy.documentAvailable
                              ? `📄 Available (${(policy.documentSize / 1024).toFixed(2)} KB)`
                              : 'Not Available'}
                          </Text>
                        </View>

                        <View style={styles.policyInfo}>
                          <Text style={styles.policyLabel}>Content Hash:</Text>
                          <Text style={styles.policyValue} numberOfLines={1} ellipsizeMode="middle">
                            {policy.contentHash.substring(0, 20)}...
                          </Text>
                        </View>

                        {/* View Policy Button */}
                        {policy.documentAvailable && (
                          <TouchableOpacity
                            onPress={async () => {
                              try {
                                console.log('📄 [ManagePolicy] Fetching PDF for policy:', policy.id);

                                // Fetch the PDF with proper authorization headers
                                const response = await fetch(
                                  `${getConfig().apiBaseUrl}/api/policies/${policy.id}/document`,
                                  {
                                    headers: {
                                      'Authorization': `Bearer ${tokens?.oauthToken}`
                                    }
                                  }
                                );

                                if (!response.ok) {
                                  throw new Error(`Failed to fetch PDF: ${response.status}`);
                                }

                                // Get the PDF blob
                                const blob = await response.blob();
                                console.log('📄 [ManagePolicy] PDF blob received:', {
                                  size: `${(blob.size / 1024).toFixed(2)} KB`,
                                  type: blob.type
                                });

                                // Create a blob URL and open in new window
                                const blobUrl = URL.createObjectURL(blob);
                                const newWindow = window.open(blobUrl, '_blank', 'noopener,noreferrer');

                                if (!newWindow) {
                                  Alert.alert('Error', 'Please allow popups to view the policy document');
                                  URL.revokeObjectURL(blobUrl);
                                  return;
                                }

                                // Clean up the blob URL after a delay
                                setTimeout(() => {
                                  URL.revokeObjectURL(blobUrl);
                                }, 1000);

                                console.log('✅ [ManagePolicy] PDF opened in new window');
                              } catch (error) {
                                console.error('❌ [ManagePolicy] Failed to open PDF:', error);
                                Alert.alert('Error', 'Failed to open policy document');
                              }
                            }}
                            style={styles.viewPolicyButton}
                          >
                            <Text style={styles.viewPolicyButtonText}>📄 View Policy</Text>
                          </TouchableOpacity>
                        )}
                      </>
                    ) : (
                      <Text style={styles.noPolicyText}>No policy available</Text>
                    )}
                  </View>
                );
              })}
            </View>
          )}
        </Card>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5'
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0'
  },
  backButton: {
    marginRight: 16
  },
  backButtonText: {
    fontSize: 16,
    color: '#007AFF',
    fontWeight: '600'
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#000'
  },
  scrollView: {
    flex: 1
  },
  scrollContent: {
    padding: 16
  },
  section: {
    marginBottom: 16,
    padding: 16
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#000'
  },
  toggleButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#007AFF',
    borderRadius: 8
  },
  toggleButtonText: {
    color: '#fff',
    fontWeight: '600'
  },
  form: {
    marginTop: 16
  },
  formGroup: {
    marginBottom: 16
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    backgroundColor: '#fff'
  },
  textArea: {
    minHeight: 150,
    textAlignVertical: 'top'
  },
  pickerContainer: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    backgroundColor: '#fff'
  },
  picker: {
    padding: 12,
    fontSize: 14,
    border: 'none',
    width: '100%',
    backgroundColor: 'transparent'
  },
  submitButton: {
    backgroundColor: '#007AFF',
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 8
  },
  submitButtonDisabled: {
    opacity: 0.5
  },
  submitButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600'
  },
  loader: {
    marginVertical: 32
  },
  policiesGrid: {
    gap: 16,
    marginTop: 16
  },
  policyCard: {
    backgroundColor: '#f9f9f9',
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0'
  },
  policyType: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#000',
    marginBottom: 12
  },
  policyInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8
  },
  policyLabel: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500'
  },
  policyValue: {
    fontSize: 14,
    color: '#000',
    fontWeight: '600'
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    backgroundColor: '#f0f0f0'
  },
  statusBadgeActive: {
    backgroundColor: '#E8F5E9'
  },
  statusText: {
    fontSize: 12,
    color: '#666',
    fontWeight: '600'
  },
  statusTextActive: {
    color: '#4CAF50'
  },
  policyContent: {
    marginTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
    paddingTop: 12
  },
  contentScroll: {
    maxHeight: 200,
    marginTop: 8
  },
  contentText: {
    fontSize: 13,
    color: '#333',
    lineHeight: 20
  },
  noPolicyText: {
    fontSize: 14,
    color: '#999',
    fontStyle: 'italic',
    textAlign: 'center',
    marginVertical: 16
  },
  helperText: {
    fontSize: 12,
    color: '#666',
    marginBottom: 8,
    fontStyle: 'italic'
  },
  uploadButton: {
    borderWidth: 2,
    borderColor: '#007AFF',
    borderStyle: 'dashed',
    borderRadius: 8,
    padding: 24,
    alignItems: 'center',
    backgroundColor: '#F8F9FA'
  },
  uploadButtonText: {
    fontSize: 16,
    color: '#007AFF',
    fontWeight: '600'
  },
  filePreview: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    backgroundColor: '#E8F5E9',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#4CAF50'
  },
  fileInfo: {
    flex: 1
  },
  fileName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#000',
    marginBottom: 4
  },
  fileSize: {
    fontSize: 12,
    color: '#666'
  },
  removeButton: {
    padding: 8,
    marginLeft: 12
  },
  removeButtonText: {
    fontSize: 14,
    color: '#F44336',
    fontWeight: '600'
  },
  viewPolicyButton: {
    backgroundColor: '#007AFF',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 12
  },
  viewPolicyButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600'
  }
});

